import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";

const STALE_DAYS = 14;

export default function InterestsCheckIn({ preferences, confirmedAt }) {
    const nav = useNavigate();
    const qc = useQueryClient();
    const [dismissed, setDismissed] = useState(false);
    if (!preferences?.length || dismissed) return null;
    const stale = !confirmedAt || Date.now() - new Date(confirmedAt).getTime() > STALE_DAYS * 864e5;
    if (!stale) return null;

    const keep = async () => {
        try {
            await api.post("/me/preferences/confirm");
            toast.success("Great — we'll keep matching you on these.");
            qc.invalidateQueries({ queryKey: ["me-full"] });
        } catch {
            toast.error("Couldn't save right now. Try again.");
        }
        setDismissed(true);
    };

    return (
        <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-accent/30 bg-accent/5 p-5" data-testid="interests-checkin">
            <div className="flex items-start gap-3">
                <div className="grid h-10 w-10 flex-none place-items-center rounded-2xl bg-accent/15 text-accent"><Sparkles className="h-5 w-5" /></div>
                <div className="flex-1">
                    <p className="font-display text-lg font-700 text-primary">Are you still interested in these activities?</p>
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="interests-checkin-chips">
                        {preferences.map((p) => (
                            <span key={p.activity_id} className="inline-flex items-center gap-1 rounded-full bg-card px-3 py-1 text-sm font-600 text-primary shadow-sm">
                                <span>{p.emoji}</span> {p.name}
                            </span>
                        ))}
                    </div>
                    <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                        <Button onClick={keep} data-testid="interests-keep" className="rounded-full bg-primary hover:bg-primary/90">Yes, keep my activities</Button>
                        <Button variant="outline" onClick={() => nav("/profile?tab=preferences")} data-testid="interests-update" className="rounded-full">Update my activities</Button>
                    </div>
                </div>
            </div>
        </motion.section>
    );
}
